import { takeLatest, put, call, select, race, take } from 'redux-saga/effects';
import get from 'lodash/get';

import {
  redirectToHealthBoard,
  redirectToProviders,
  redirectToRescheduleProviderDetails,
  redirectToUsers,
} from '../../../utils/appointments/redirect-utils';
import {
  APPOINTMENT_ERRORS,
  ERROR_CODES,
  CHANGE_APPOINTMENT_STATUS_ERROR_CODES,
} from '../../../constants/errors';
import * as confirmVisitSelfScheduling from '../requests/confirm-visit-selfScheduling-req';
import * as confirmVisitGW from '../requests/confirm-visit-GW-req';
import * as confirmInlineVisit from '../requests/confirm-inline-visit';
import * as inlineVisitTimeSlots from '../requests/inline-visits-time-slots';
import * as editProfiles from '../../profiles/edit-user';
import { consts as createEirUrgentCareAppointmentConsts } from '../requests/create-eir-urgent-care-appointment';
import { appointmentProgressActions } from '../progress-types';
import {
  selectInlineVisitTimeSlots,
  selectProgressCareUser,
  selectProgressIsCreateFlowGW,
  selectProgressIsRescheduleData,
  selectProgressLocation,
  selectProgressPatientForm,
  selectProgressSchedule,
  selectProgressService,
  selectReasonForOverview,
} from '../selectors';
import { CustomEvents, GATrackCustomEvent } from '../../../gtag';

function* handleFailAppointmentConfirm({ payload }) {
  const code = get(payload, 'error.code');
  const error = get(APPOINTMENT_ERRORS, code, 'errors.common');
  const description = get(payload, 'error.description');
  const isGWFlow = yield select(selectProgressIsCreateFlowGW);
  const isRescheduleFlow = yield select(selectProgressIsRescheduleData);
  const scheduleData = yield select(selectProgressSchedule);

  const errorMessage = CHANGE_APPOINTMENT_STATUS_ERROR_CODES.includes(code)
    ? description
    : error;

  yield put(
    isGWFlow
      ? confirmVisitGW.actions.showErrorAlert(errorMessage)
      : confirmVisitSelfScheduling.actions.showErrorAlert(
          'errors.appointment.timeSlotNotAvailable',
        ),
  );

  if (isRescheduleFlow) {
    const {
      provider: { id: providerId },
      location: { id: locationId },
    } = scheduleData || { provider: {}, location: {} };

    yield call(redirectToRescheduleProviderDetails, { providerId, locationId });
  } else {
    const redirectCb = [
      ERROR_CODES.APPOINTMENT_SCHEDULE_TIME_NOT_AVAILABLE,
      ERROR_CODES.PROVIDER_IS_NOT_AVAILABLE_FOR_THIS_TIME,
    ].includes(code)
      ? redirectToProviders
      : redirectToUsers;

    yield call(redirectCb);
  }
}

function* trackCompleteScheduling({ payload }) {
  const visitCode = get(payload, 'visitType.code');
  yield call(() =>
    GATrackCustomEvent(CustomEvents.GET_CARE_COMPLETE, { visitCode }),
  );
}

function* handleSuccessAppointmentConfirm() {
  const { id } = yield select(selectProgressCareUser);

  const patientForm = yield select(selectProgressPatientForm);

  const careUser = yield select(selectProgressCareUser);

  const raceValue = get(patientForm, 'race', null);
  const ethnicity = get(patientForm, 'ethnicity', null);
  const language = get(patientForm, 'language', null);
  const homePhone = get(patientForm, 'homePhone', null);
  const postalCode = get(patientForm, 'postalCode', null);

  if (
    race !== careUser.race ||
    ethnicity !== careUser.ethnicity ||
    language !== careUser.language ||
    homePhone !== careUser.homePhone ||
    postalCode !== careUser.postalCode
  ) {
    yield put(
      editProfiles.actions.send({
        id: careUser.id,
        ...(raceValue && { race: raceValue }),
        ...(ethnicity && { ethnicity }),
        ...(language && { language }),
        ...(homePhone && { homePhone }),
        ...(postalCode && { postalCode }),
      }),
    );
  }

  yield call(redirectToHealthBoard, id);
}

function* handleFailInlineVisitConfirm() {
  const reason = yield select(selectReasonForOverview);
  const service = yield select(selectProgressService);
  const location = yield select(selectProgressLocation);
  yield put(
    confirmInlineVisit.actions.showErrorAlert(
      'errors.appointment.timeSlotNotAvailable',
    ),
  );
  yield put(
    inlineVisitTimeSlots.actions.send({
      reasonTypeId: reason.id,
      serviceId: service.id,
      locationId: location.id,
    }),
  );

  const { succeed } = yield race({
    succeed: take(inlineVisitTimeSlots.consts.SUCCEED),
    rejected: take(inlineVisitTimeSlots.consts.REJECTED),
  });

  if (succeed) {
    const timeSlots = yield select(selectInlineVisitTimeSlots);

    if (timeSlots.length > 0) {
      yield put(
        appointmentProgressActions.setScheduleData({ time: timeSlots[0] }),
      );
    }
  } else {
    yield put(confirmInlineVisit.actions.showErrorAlert('errors.common'));
  }
}

export default function* confirmAppointmentSaga() {
  yield takeLatest(
    [
      confirmVisitGW.consts.SUCCEED,
      confirmVisitSelfScheduling.consts.SUCCEED,
      createEirUrgentCareAppointmentConsts.SUCCEED,
    ],
    handleSuccessAppointmentConfirm,
  );
  yield takeLatest(
    [
      confirmVisitGW.consts.SUCCEED,
      confirmInlineVisit.consts.SUCCEED,
      confirmVisitSelfScheduling.consts.SUCCEED,
      createEirUrgentCareAppointmentConsts.SUCCEED,
    ],
    trackCompleteScheduling,
  );

  yield takeLatest(
    [
      confirmVisitGW.consts.REJECTED,
      confirmVisitSelfScheduling.consts.REJECTED,
      createEirUrgentCareAppointmentConsts.REJECTED,
    ],
    handleFailAppointmentConfirm,
  );
  yield takeLatest(
    confirmInlineVisit.consts.REJECTED,
    handleFailInlineVisitConfirm,
  );
}



// WEBPACK FOOTER //
// ./src/redux/appointmentForm/sagas/confirm-appointment-saga.js